import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  cableConstructionSchema,
  conductorMaterialSchema,
  insulationMaterialSchema,
} from "../services/cableContract";
import { cableTrayRunCreateRequestSchema, type CableTrayRunCreateRequest } from "../services/cableTray";
import {
  allowanceBasisSchema,
  coverKindSchema,
  ratedLoadBasisSchema,
  trayArrangementSchema,
  trayTypeSchema,
} from "../services/cableTrayContract";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { CableRunSelector } from "./CableRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker } from "./SldElementPicker";
import {
  ALLOWANCE_BASIS_LABELS,
  ARRANGEMENT_LABELS,
  buildCableTrayRunPayload,
  CABLE_SOURCE_LABELS,
  CABLE_SOURCES,
  CABLE_TRAY_DRAFT_MODULE,
  CABLE_TRAY_LABELS,
  cableTrayDraftSchema,
  CONDUCTOR_MATERIAL_LABELS,
  CONSTRUCTION_LABELS,
  COVER_LABELS,
  createCableRowDraft,
  createInitialCableTrayDraft,
  INSULATION_LABELS,
  RATED_LOAD_BASIS_LABELS,
  seedCableRowIdCounter,
  TRAY_TYPE_LABELS,
  type CableRowDraft,
  type CableSourceChoice,
  type CableTrayDraft,
} from "./cableTrayDraft";

/** The first option of every choice: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export { CABLE_RUN_HINT, NO_CABLE_RUNS_NOTE, cableRunOptionText } from "./CableRunSelector";

export const DIAMETER_HINT =
  "The fill uses the cable's overall diameter from its data sheet, never the conductor area; the " +
  "load uses the mass per metre from the same sheet. A stored cable run holds neither, so both " +
  "are declared here with their source. Without a diameter the fill is not checked; without a " +
  "mass the load is not checked.";

export const FILL_LIMIT_HINT =
  "The fill limit is standard- or project-defined and must be declared with its source; the " +
  "product carries no limit of its own (no NEC 392, IEC 61537, IS or CPWD value). Without a " +
  "limit the fill is stated and nothing is compared. A single layer is judged by the usable " +
  "width instead.";

export const RATED_LOAD_HINT =
  "The tray's rated load is the manufacturer's declared figure at the span it is stated for; a " +
  "support spacing longer than that span leaves the load not checked (review required). A " +
  "rating per span is divided by its span. The declared future allowance scales the fill and the " +
  "load alike; it scales a single layer's width only when it is declared to apply to the width.";

export const PROFILE_NOTE =
  "No profile holds a tray table in this version: every limit is the engineer's declared figure " +
  "with its source (GAP-028); the arithmetic is universal. The profile is recorded with the run " +
  "and must match the project revision.";

type CableTrayFormProps = {
  disabled?: boolean;
  onSubmit: (payload: CableTrayRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): each row's feeder
  // is picked from it.
  sldElements?: SldElements | null | undefined;
};

// The cable tray study of one route (EOS-12). The form owns the draft; each row's cable starts
// on a linked cable run of the active open revision, its installation facts read by the backend
// when the study is saved. Every choice starts unchosen: the backend imposes no default.
export function CableTrayForm({ disabled = false, onSubmit, sldElements }: CableTrayFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<CableTrayDraft>({
      module: CABLE_TRAY_DRAFT_MODULE,
      createInitial: createInitialCableTrayDraft,
      schema: cableTrayDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current?.querySelector<HTMLInputElement>(`input[name="${rowToFocus}-code"]`)?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<CableTrayDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateRow(id: string, patch: Partial<CableRowDraft>) {
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  // Stable per row for the cable selector's effect that clears an unlisted run.
  const setCableRunId = useCallback(
    (id: string, cableRunId: string) =>
      setDraft((current) => ({
        ...current,
        rows: current.rows.map((row) => (row.id === id ? { ...row, cableRunId } : row)),
      })),
    [setDraft],
  );

  function addRow() {
    seedCableRowIdCounter(draft.rows.map((row) => row.id));
    const row = createCableRowDraft();
    setDraft((current) => ({ ...current, rows: [...current.rows, row] }));
    setRowToFocus(row.id);
  }

  function removeRow(id: string) {
    setDraft((current) => ({ ...current, rows: current.rows.filter((row) => row.id !== id) }));
  }

  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "cable_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, CABLE_TRAY_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = cableTrayRunCreateRequestSchema.safeParse(buildCableTrayRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the cable tray inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof CableTrayDraft,
    name: string,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<CableTrayDraft>)}
      />
    </label>
  );

  const studyChoice = <T extends string>(
    label: string,
    field: keyof CableTrayDraft,
    name: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<CableTrayDraft>)}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  // Field names carry the row's id ("tray-row-3-code"), stable across add and remove.
  const rowText = (
    row: CableRowDraft,
    label: string,
    field: keyof CableRowDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      />
    </label>
  );

  const rowChoice = <T extends string>(
    row: CableRowDraft,
    label: string,
    field: keyof CableRowDraft,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const singleLayer = draft.arrangement !== "" && draft.arrangement !== "MULTI_LAYER";

  return (
    <form aria-label="Cable tray inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Route definition</legend>
        {studyText("Route code", "code", "routeCode")}
        {studyText("Route name", "name", "routeName")}
        {studyChoice("Tray type", "trayType", "trayType", trayTypeSchema.options, TRAY_TYPE_LABELS)}
        {studyChoice("Cable arrangement", "arrangement", "arrangement", trayArrangementSchema.options, ARRANGEMENT_LABELS)}
        {studyText("Usable width (mm)", "usableWidthMm", "usableWidthMm", "decimal")}
        {studyText("Usable depth (mm)", "usableDepthMm", "usableDepthMm", "decimal")}
        {studyText("Dimensions source", "dimensionsSource", "dimensionsSource")}
        {studyChoice("Cover", "cover", "cover", coverKindSchema.options, COVER_LABELS)}
        {studyText("Feeders carried (SLD)", "feeders", "feeders")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Fill limit</legend>
        {studyText("Fill limit (%)", "fillLimitPercent", "fillLimitPercent", "decimal")}
        {studyText("Fill limit source", "fillLimitSource", "fillLimitSource")}
        <p data-fill-limit-hint="true">{FILL_LIMIT_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Load rating</legend>
        {studyText("Rated load (kg)", "ratedLoadKg", "ratedLoadKg", "decimal")}
        {studyChoice("Rated load basis", "ratedLoadBasis", "ratedLoadBasis", ratedLoadBasisSchema.options, RATED_LOAD_BASIS_LABELS)}
        {studyText("Rated span (m)", "ratedSpanM", "ratedSpanM", "decimal")}
        {studyText("Rated load source", "ratedLoadSource", "ratedLoadSource")}
        {studyText("Support spacing (m)", "supportSpacingM", "supportSpacingM", "decimal")}
        {studyText("Support spacing source", "supportSpacingSource", "supportSpacingSource")}
        {studyText("Future allowance (%)", "futureAllowancePercent", "futureAllowancePercent", "decimal")}
        {studyText("Future allowance source", "futureAllowanceSource", "futureAllowanceSource")}
        {studyChoice("Future allowance applies to", "futureAllowanceBasis", "futureAllowanceBasis", allowanceBasisSchema.options, ALLOWANCE_BASIS_LABELS)}
        <p data-rated-load-hint="true">{RATED_LOAD_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Cable rows</legend>
        {draft.rows.map((row, index) => {
          const number = index + 1;
          const linkedCable = row.cableSource === "LINKED";
          return (
            <fieldset key={row.id} data-tray-row={row.id}>
              <legend>Row {number}</legend>

              <fieldset>
                <legend>Row</legend>
                {rowText(row, "Row code", "code", "text")}
                {rowText(row, "Row name", "name", "text")}
                <SldElementPicker
                  label="Feeder (SLD)"
                  name={`${row.id}-sldElementCode`}
                  value={row.sldElementCode}
                  onChange={(value) => updateRow(row.id, { sldElementCode: value })}
                  categories={["feeder"]}
                  elements={sldElements}
                />
              </fieldset>

              <fieldset>
                <legend>Cable</legend>
                <label>
                  Cable source
                  <select
                    name={`${row.id}-cableSource`}
                    value={row.cableSource}
                    onChange={(event) =>
                      updateRow(row.id, { cableSource: event.target.value as CableSourceChoice })
                    }
                  >
                    {CABLE_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {CABLE_SOURCE_LABELS[source]}
                      </option>
                    ))}
                  </select>
                </label>
                {linkedCable ? (
                  revisionId === null ? null : (
                    <CableRunSelector
                      revisionId={revisionId}
                      name={`${row.id}-cableRunId`}
                      cableRunId={row.cableRunId}
                      onChange={(cableRunId) => setCableRunId(row.id, cableRunId)}
                    />
                  )
                ) : (
                  <>
                    {rowChoice(row, "Construction", "construction", cableConstructionSchema.options, CONSTRUCTION_LABELS)}
                    {rowText(row, "Cores", "cores")}
                    {rowChoice(row, "Conductor material", "conductorMaterial", conductorMaterialSchema.options, CONDUCTOR_MATERIAL_LABELS)}
                    {rowChoice(row, "Insulation", "insulationMaterial", insulationMaterialSchema.options, INSULATION_LABELS)}
                    {rowText(row, "Cable source text", "cableSourceText", "text")}
                  </>
                )}
                {rowText(row, "Number of cables", "quantity")}
                {rowText(row, "Quantity source", "quantitySource", "text")}
              </fieldset>

              <fieldset>
                <legend>Diameter, mass and spacing</legend>
                {rowText(row, "Overall diameter D (mm)", "overallDiameterMm")}
                {rowText(row, "Diameter source", "diameterSource", "text")}
                {rowText(row, "Mass per metre (kg/m)", "massKgPerM")}
                {rowText(row, "Mass source", "massSource", "text")}
                {singleLayer ? (
                  <>
                    {rowText(row, "Clear spacing (mm)", "spacingMm")}
                    {rowText(row, "Clear spacing (diameters)", "spacingInDiameters")}
                    {rowText(row, "Spacing source", "spacingSource", "text")}
                  </>
                ) : null}
                <p data-diameter-hint="true">{DIAMETER_HINT}</p>
              </fieldset>

              {draft.rows.length > 1 ? (
                <button type="button" onClick={() => removeRow(row.id)}>
                  Remove row {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addRow}>
          Add row
        </button>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check cable tray
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default CableTrayForm;
